import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAllAchievements, getCircleBgCatalogue, getProfileSkins, searchFurniItems } from '../../services/api';
import { resolveCosmeticUrl } from '../../services/profileColorService';
import { getBadgeUrl } from '../../utils/badgeUrl';
import { buildRenderUrl } from './values/valuesShared';

export function useCosmeticCatalogues() {
    const [catalogues, setCatalogues] = useState({ badge: [], circle_bg: [], profile_skin: [] });

    useEffect(() => {
        let active = true;

        Promise.all([
            getAllAchievements().catch(() => null),
            getCircleBgCatalogue().catch(() => null),
            getProfileSkins().catch(() => null),
        ]).then(([achRes, bgRes, skinRes]) => {
            if (!active) return;

            const achievements = achRes?.achievements || achRes?.badges || [];
            const backgrounds = bgRes?.backgrounds || [];
            const skins = skinRes?.skins || [];

            setCatalogues({
                badge: achievements
                    .map(a => {
                        const id = a.badgeID || a.badgeId || a.BadgeID;
                        const image = id ? getBadgeUrl(id, !!(a.uploaded ?? a.Uploaded)) : '';
                        return { id, label: a.name || a.Name || '', image, shopImage: image, fit: 'contain' };
                    })
                    .filter(o => o.id),
                circle_bg: backgrounds
                    .map(b => {
                        const image = resolveCosmeticUrl(b.imageURL || '');
                        return { id: b.BgID || b.bgId, label: b.name || '', image, shopImage: image, fit: 'cover' };
                    })
                    .filter(o => o.id),
                profile_skin: skins
                    .filter(sk => sk.id && sk.id !== 'default')
                    .map(sk => {
                        const firstAsset = (sk.assets || []).find(a => a.image)?.image || '';
                        return {
                            id: sk.id,
                            label: sk.name || '',
                            image: resolveCosmeticUrl(sk.backgroundImage || ''),
                            fit: 'repeat',
                            shopImage: resolveCosmeticUrl(firstAsset || sk.backgroundImage || ''),
                        };
                    }),
            });
        });

        return () => { active = false; };
    }, []);

    return catalogues;
}

export function CosmeticPicker({ options, value, onChange, onPick, placeholder }) {
    const known = options.some(o => o.id === value);
    const chosen = options.find(o => o.id === value) || null;

    const thumbStyle = chosen?.image
        ? (chosen.fit === 'repeat'
            ? { backgroundImage: `url(${chosen.image})`, backgroundRepeat: 'repeat', backgroundSize: 'auto' }
            : { backgroundImage: `url(${chosen.image})`, backgroundSize: chosen.fit || 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' })
        : undefined;

    return (
        <div className="flex flex-1 items-center gap-2">
            <div
                className="h-[34px] w-[34px] shrink-0 rounded border border-gray-300 bg-gray-100"
                style={thumbStyle}
                title={chosen ? `${chosen.label || chosen.id}` : 'No preview'}
            />
            <select
                value={known ? value : ''}
                onChange={(e) => {
                    const next = e.target.value;
                    onChange(next);
                    if (onPick) onPick(options.find(o => o.id === next) || null);
                }}
                className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm"
            >
                <option value="">
                    {options.length ? 'Pick one' : 'Loading...'}
                </option>
                {options.map(o => (
                    <option key={o.id} value={o.id}>
                        {o.label ? `${o.label} (${o.id})` : o.id}
                    </option>
                ))}
            </select>
            <input
                type="text"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                title="Or type an ID manually"
                className="w-[130px] shrink-0 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
            />
        </div>
    );
}

export const furniImageFor = (item) =>
    item?.iconImg || item?.smallImg || buildRenderUrl(item?.code, 'icon', 0) || '';

export function FurniPicker({ value, onPick }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [open, setOpen] = useState(false);
    const abortRef = useRef(null);

    const trimmed = useMemo(() => query.trim(), [query]);

    useEffect(() => {
        if (trimmed.length < 3) {
            setResults([]);
            setSearching(false);
            return undefined;
        }

        const timer = setTimeout(async () => {
            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setSearching(true);
            try {
                const res = await searchFurniItems(trimmed, controller.signal);
                setResults(res?.items || []);
                setOpen(true);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 250);

        return () => clearTimeout(timer);
    }, [trimmed]);

    const chosenImage = value?.furniImage || buildRenderUrl(value?.furniCode, 'icon', 0);

    return (
        <div className="space-y-2">
            {value?.furniCode && (
                <div className="flex items-center gap-2 border border-coal/15 rounded p-2 bg-coal/5">
                    {chosenImage && (
                        <img src={chosenImage} alt={value.furniName || value.furniCode} className="h-8 w-8 object-contain" />
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-sm text-coal truncate">{value.furniName || value.furniCode}</div>
                        <div className="text-xs text-coal/50 font-mono truncate">{value.furniCode}</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onPick(null)}
                        className="px-2 py-1 rounded bg-coal/10 hover:bg-coal/20 text-xs"
                    >
                        Change
                    </button>
                </div>
            )}

            {!value?.furniCode && (
                <>
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search furni by name or code, e.g. pumpkin lamp"
                        className="w-full border border-coal/20 rounded px-2 py-1 text-sm text-coal"
                    />
                    {trimmed.length > 0 && trimmed.length < 3 && (
                        <div className="text-xs text-coal/40">Type at least 3 characters.</div>
                    )}
                    {searching && <div className="text-xs text-coal/40">Searching...</div>}
                    {open && !searching && results.length === 0 && trimmed.length >= 3 && (
                        <div className="text-xs text-coal/40">No furni matched that.</div>
                    )}
                    {results.length > 0 && (
                        <div className="max-h-56 overflow-y-auto border border-coal/15 rounded divide-y divide-coal/10">
                            {results.map(item => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => {
                                        onPick({
                                            furniId: item.id,
                                            furniCode: item.code,
                                            furniName: item.name,
                                            furniImage: furniImageFor(item),
                                        });
                                        setQuery('');
                                        setResults([]);
                                        setOpen(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-2 py-1 text-left hover:bg-coal/5"
                                >
                                    <img
                                        src={furniImageFor(item)}
                                        alt={item.name}
                                        className="h-8 w-8 object-contain shrink-0"
                                    />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm text-coal truncate">{item.name}</span>
                                        <span className="block text-xs text-coal/50 font-mono truncate">{item.code}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
